import React from 'react'
import { Button } from '@/common/ui/button'
import { H3 } from '@/common/ui'
import SolidBackgroundModal from '@/common/ui/SolidBackgroundModal'
import { IntercomWidget } from '@/common/intercom/IntercomWidget'
import { ProgressBarWithBackSupport } from '@/common/ui/progressBar'
import { useOnboardingPageTemplateLogic } from './useOnboardingPageTemplateLogic'

type SecondaryAction = {
    label: string
    onClick: () => void
    variant?: 'ghost' | 'outline' | 'link'
}

type Props = {
    title: string
    description?: string | React.ReactNode
    children: React.ReactNode
    onSubmit?: (e: React.FormEvent<HTMLFormElement>) => void
    cta?: string
    isLoading?: boolean
    onBack?: () => void
    showChatWidget?: boolean
    progress: number
    disabled?: boolean
    /** Optional secondary action button (e.g., "Skip" button). Displays alongside the primary CTA. */
    secondaryAction?: SecondaryAction
    /** Hide the action buttons area completely (useful when child components manage their own actions) */
    hideActions?: boolean
    modalClassName?: string
}

/** Onboarding page template — wires useOnboardingPageTemplateLogic for debounced form submission.
 *  Provides consistent layout, progress bar, and form controls for all onboarding steps.
 */
export function Onboarding({
    title,
    description,
    children,
    onSubmit,
    cta = 'Next',
    isLoading,
    onBack,
    showChatWidget = true,
    progress,
    disabled = false,
    secondaryAction,
    hideActions = false,
    modalClassName,
}: Props) {
    const { handleSubmit } = useOnboardingPageTemplateLogic({ onSubmit })

    return (
        <SolidBackgroundModal className={modalClassName}>
            <ProgressBarWithBackSupport progress={progress} onBack={onBack} className="mb-6 md:mb-12" />
            <H3 className="sm:text-center text-3xl mb-4">{title}</H3>
            {description && (
                <p className="sm:text-center font-normal text-[16px] leading-[1.4] font-inter break-words text-[#737671] sm:text-black mb-6">
                    {description}
                </p>
            )}
            <form onSubmit={handleSubmit} className="z-0 w-full h-full max-w-xl">
                <div
                    className={
                        hideActions
                            ? 'w-full h-full mb-auto flex flex-col gap-4'
                            : 'pb-28 md:pb-10 w-full mb-auto flex flex-col gap-4'
                    }
                >
                    {children}
                </div>
                {!hideActions && (
                    <div
                        className="
                            w-full bg-white p-6 flex flex-col gap-2.5 z-10
                            fixed bottom-0 left-0 sm:static sm:p-0 sm:bg-transparent
                            shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.1)] sm:shadow-none"
                    >
                        <div
                            className={`flex w-full items-center gap-2.5 ${onBack || secondaryAction ? 'justify-between' : 'justify-end'}`}
                        >
                            {onBack && (
                                <div className="hidden sm:block">
                                    <Button onClick={onBack} variant={'outline'} className="h-14" type="button">
                                        Back
                                    </Button>
                                </div>
                            )}
                            {secondaryAction && (
                                <Button
                                    onClick={secondaryAction.onClick}
                                    variant={secondaryAction.variant || 'ghost'}
                                    className="h-14 border-0 w-full sm:w-auto"
                                    type="button"
                                >
                                    {secondaryAction.label}
                                </Button>
                            )}
                            <Button
                                autoFocus
                                disabled={disabled}
                                loading={isLoading}
                                variant={'secondary'}
                                type="submit"
                                className="w-full sm:w-auto h-[60px] sm:min-w-[130px]"
                            >
                                {cta}
                            </Button>
                        </div>
                    </div>
                )}
            </form>
            {showChatWidget && <IntercomWidget />}
        </SolidBackgroundModal>
    )
}
